import React from 'react';

export const renderInlineNode = (node: any, index: number): React.ReactNode => {
  if (!node) return null;

  switch (node.nodeType) {
    case 'text':
      let text = node.value;
      if (node.marks && node.marks.length > 0) {
        node.marks.forEach((mark: any) => {
          switch (mark.type) {
            case 'bold':
              text = <strong key={index} className="font-black">{text}</strong>;
              break;
            case 'italic':
              text = <em key={index}>{text}</em>;
              break;
            case 'underline':
              text = <u key={index}>{text}</u>;
              break;
            case 'code':
              text = <code key={index} className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">{text}</code>;
              break;
          }
        });
      }
      return text;
    case 'hyperlink':
      return (
        <a
          key={index}
          href={node.data?.uri}
          target="_blank"
          rel="noopener noreferrer"
          className="text-yellow-500 hover:text-yellow-600 underline font-black"
        >
          {node.content?.map((inline: any, i: number) => renderInlineNode(inline, i))}
        </a>
      );
    default:
      return node.value || null;
  }
};

interface RichTextRendererProps {
  document: any;
  assets?: any[];
}

export const RichTextRenderer: React.FC<RichTextRendererProps> = ({ document, assets }) => {
  if (!document || !document.content) {
    return null;
  }

  return (
    <>
      {document.content.map((node: any, index: number) => {
        switch (node.nodeType) {
          case 'paragraph':
            return (
              <p key={index} className="mb-4 font-body">
                {node.content?.map((inline: any, i: number) => renderInlineNode(inline, i))}
              </p>
            );
          case 'heading-1':
            return (
              <h1 key={index} className="text-5xl md:text-6xl font-display font-black mb-6 mt-8 uppercase tracking-tight">
                {node.content?.map((inline: any, i: number) => renderInlineNode(inline, i))}
              </h1>
            );
          case 'heading-2':
            return (
              <h2 key={index} className="text-4xl md:text-5xl font-display font-black mb-4 mt-6 uppercase tracking-tight">
                {node.content?.map((inline: any, i: number) => renderInlineNode(inline, i))}
              </h2>
            );
          case 'heading-3':
            return (
              <h3 key={index} className="text-3xl md:text-4xl font-headline font-bold mb-3 mt-5 uppercase">
                {node.content?.map((inline: any, i: number) => renderInlineNode(inline, i))}
              </h3>
            );
          case 'heading-4':
            return (
              <h4 key={index} className="text-2xl md:text-3xl font-headline font-bold mb-2 mt-4 uppercase">
                {node.content?.map((inline: any, i: number) => renderInlineNode(inline, i))}
              </h4>
            );
          case 'heading-5':
            return (
              <h5 key={index} className="text-xl md:text-2xl font-headline font-bold mb-2 mt-3">
                {node.content?.map((inline: any, i: number) => renderInlineNode(inline, i))}
              </h5>
            );
          case 'heading-6':
            return (
              <h6 key={index} className="text-lg md:text-xl font-headline font-bold mb-2 mt-3">
                {node.content?.map((inline: any, i: number) => renderInlineNode(inline, i))}
              </h6>
            );
          case 'unordered-list':
            return (
              <ul key={index} className="list-disc list-inside mb-4 space-y-2 font-body">
                {node.content?.map((item: any, i: number) => (
                  <li key={i}>
                    {item.content?.[0]?.content?.map((inline: any, j: number) =>
                      renderInlineNode(inline, j)
                    )}
                  </li>
                ))}
              </ul>
            );
          case 'ordered-list':
            return (
              <ol key={index} className="list-decimal list-inside mb-4 space-y-2 font-body">
                {node.content?.map((item: any, i: number) => (
                  <li key={i}>
                    {item.content?.[0]?.content?.map((inline: any, j: number) =>
                      renderInlineNode(inline, j)
                    )}
                  </li>
                ))}
              </ol>
            );
          case 'blockquote':
            return (
              <blockquote key={index} className="border-l-4 border-yellow-500 pl-4 italic my-4 font-body">
                {node.content?.map((block: any, i: number) => (
                  <RichTextRenderer key={i} document={{ content: [block] }} assets={assets} />
                ))}
              </blockquote>
            );
          case 'hr':
            return <hr key={index} className="my-8 border-gray-300" />;
          case 'embedded-asset-block':
            const assetId = node.data?.target?.sys?.id;
            const asset = assets?.find((a: any) => a.sys.id === assetId);
            if (asset && asset.fields?.file) {
              const imageUrl = asset.fields.file.url.startsWith('//')
                ? `https:${asset.fields.file.url}`
                : asset.fields.file.url;
              return (
                <figure key={index} className="my-8">
                  <img
                    src={imageUrl}
                    alt={asset.fields.title || asset.fields.description || 'Embedded image'}
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                  {asset.fields.description && (
                    <figcaption className="text-sm text-gray-600 mt-2 text-center italic font-body">
                      {asset.fields.description}
                    </figcaption>
                  )}
                </figure>
              );
            }
            return null;
          case 'embedded-entry-block':
            return null;
          default:
            return null;
        }
      })}
    </>
  );
};

// Utility wrapper used by some unit tests: return the rendered nodes or null
export function renderRichText(document: any, assets?: any[]) {
  if (!document || !document.content) return null;
  return <RichTextRenderer document={document} assets={assets} />;
}